import { PairingBundle } from './types';

const STORAGE_KEY = 'domiHealthPairingBundle';

export class InvalidPairingBundleError extends Error {}

/** Validates and normalizes whatever was scanned/pasted from the edge
 * node's pairing QR code. Defensive on purpose: this JSON comes from
 * scanning an arbitrary QR code (or manual paste), and a malformed or
 * malicious payload should fail cleanly here rather than produce a
 * confusing runtime error later, once its `token` is already being sent
 * as a bearer credential on every request.
 */
export function parsePairingBundle(raw: string): PairingBundle {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    throw new InvalidPairingBundleError('Pairing code is not valid JSON');
  }

  if (typeof parsed !== 'object' || parsed === null) {
    throw new InvalidPairingBundleError('Pairing code is not a JSON object');
  }
  const obj = parsed as Record<string, unknown>;

  if (typeof obj.host !== 'string' || obj.host.length === 0) {
    throw new InvalidPairingBundleError('Pairing code is missing "host"');
  }
  if (typeof obj.port !== 'number' || !Number.isInteger(obj.port) || obj.port <= 0) {
    throw new InvalidPairingBundleError('Pairing code is missing a valid "port"');
  }
  if (typeof obj.token !== 'string' || obj.token.length === 0) {
    throw new InvalidPairingBundleError('Pairing code is missing "token"');
  }
  if (typeof obj.cert_spki_sha256 !== 'string' || !/^[0-9a-f]{64}$/i.test(obj.cert_spki_sha256)) {
    throw new InvalidPairingBundleError('Pairing code is missing a valid "cert_spki_sha256"');
  }

  return {
    host: obj.host,
    port: obj.port,
    token: obj.token,
    cert_spki_sha256: obj.cert_spki_sha256.toLowerCase(),
  };
}

export function getPairingBundle(): PairingBundle | null {
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    return parsePairingBundle(raw);
  } catch (err) {
    // Storage holding something we can no longer parse (e.g. an old,
    // incompatible shape) is equivalent to "not paired yet" — never
    // throw out of a plain getter.
    return null;
  }
}

export function savePairingBundle(bundle: PairingBundle): void {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(bundle));
}

export function clearPairingBundle(): void {
  window.localStorage.removeItem(STORAGE_KEY);
}
